import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Star } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * Shared layout for full-page role/career previews.
 *
 * Props:
 *   sections          → [{ id, label, icon }]   nav rail
 *   heroAccent        → { from, via, to }       gradient classes (e.g. 'from-blue-600')
 *   heroBadges        → [{ text, tone }]        chip badges below back button
 *   heroEyebrow       → small text above title (optional)
 *   title             → big role/career name
 *   description       → long descriptive paragraph
 *   onBack            → handler for the back button
 *   backLabel         → text for back link
 *   railEyebrow       → label for sticky rail (default "Jump to section")
 *   children          → ScrollSection nodes (use the exported wrappers)
 */
export const RolePreviewLayout = ({
  sections = [],
  heroAccent = { from: 'from-teal-600', via: 'via-teal-700', to: 'to-blue-700' },
  heroBadges = [],
  heroEyebrow,
  title,
  description,
  onBack,
  backLabel = 'Back',
  railEyebrow = 'Jump to section',
  children,
}) => {
  const [activeSection, setActiveSection] = useState(sections[0]?.id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.dataset.sectionId);
        });
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: 0 }
    );
    sections.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const setRef = (sid) => (el) => {
    sectionRefs.current[sid] = el;
  };
  const scrollToSection = (sid) => {
    const el = sectionRefs.current[sid];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-white" data-testid="role-preview-layout">
      {/* Hero */}
      <section
        className={`pt-24 pb-12 px-4 md:px-6 bg-gradient-to-br ${heroAccent.from} ${heroAccent.via} ${heroAccent.to} text-white relative overflow-hidden`}
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'radial-gradient(circle at 25% 30%, white 1px, transparent 1px), radial-gradient(circle at 75% 70%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="max-w-6xl mx-auto relative">
          {onBack && (
            <button
              onClick={onBack}
              data-testid="role-preview-back"
              className="inline-flex items-center gap-1.5 text-xs text-white/85 hover:text-white mb-5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full"
            >
              <ArrowLeft size={13} /> {backLabel}
            </button>
          )}
          {heroBadges.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {heroBadges.map((b, i) => (
                <Badge
                  key={i}
                  className={
                    b.tone === 'solid'
                      ? 'bg-white text-gray-900 border-0 font-bold'
                      : 'bg-white/20 backdrop-blur-sm text-white border-0'
                  }
                >
                  {b.text}
                </Badge>
              ))}
            </div>
          )}
          {heroEyebrow && (
            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-white/70 mb-2">
              {heroEyebrow}
            </p>
          )}
          <h1
            className="text-4xl md:text-6xl font-extrabold mb-4 leading-[1.05] tracking-tight"
            data-testid="role-preview-title"
          >
            {title}
          </h1>
          {description && (
            <p className="text-white/90 text-base md:text-lg max-w-3xl leading-[1.7]">
              {description}
            </p>
          )}
        </div>
      </section>

      {/* Rail + content */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 grid lg:grid-cols-[240px_1fr] gap-10 relative">
        <aside className="hidden lg:block">
          <nav className="sticky top-28 space-y-0.5" data-testid="section-rail">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2 px-3">
              {railEyebrow}
            </p>
            {sections.map((s) => {
              const Icon = s.icon;
              const isActive = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  data-testid={`rail-${s.id}`}
                  onClick={() => scrollToSection(s.id)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    isActive
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span
                    className={`w-1 h-4 rounded-full transition-all ${
                      isActive ? 'bg-teal-600' : 'bg-transparent'
                    }`}
                  />
                  {Icon && <Icon size={13} className="flex-shrink-0" />}
                  <span className="truncate">{s.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="space-y-12">
          {React.Children.map(children, (child) => {
            if (!child) return null;
            const sid = child.props['data-section-id'] || child.props.id;
            return React.cloneElement(child, { ref: setRef(sid) });
          })}
        </main>
      </div>
    </div>
  );
};

/* ── ScrollSection wrapper ── */
export const ScrollSection = React.forwardRef(({ id, children, className = '' }, ref) => (
  <motion.section
    ref={ref}
    data-section-id={id}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-100px 0px' }}
    transition={{ duration: 0.4, ease: 'easeOut' }}
    className={`scroll-mt-28 ${className}`}
  >
    {children}
  </motion.section>
));
ScrollSection.displayName = 'ScrollSection';

/* ── Common section header ── */
export const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-5 pb-3 border-b border-gray-200">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className="text-teal-600" size={18} />}
      <h2 className="text-xl md:text-2xl font-bold text-gray-900">{title}</h2>
    </div>
    {subtitle && <p className="text-xs text-gray-500 pl-7">{subtitle}</p>}
  </div>
);

/* ── Rating card with stars ── */
export const RatingCard = ({ label, value, max = 5 }) => (
  <div className="p-3 bg-white border border-gray-200 rounded-xl">
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-xs font-semibold text-gray-700">{label}</span>
      <span className="text-[10px] text-gray-500 font-mono">
        {value}/{max}
      </span>
    </div>
    <div className="flex gap-1">
      {Array.from({ length: max }, (_, i) => (
        <Star
          key={i}
          size={13}
          className={
            i + 1 <= value ? 'fill-teal-500 text-teal-500' : 'text-gray-200 fill-gray-100'
          }
        />
      ))}
    </div>
  </div>
);

/* ── Simple bullet list ── */
export const BulletList = ({ items, dotColor = 'bg-gray-400' }) => {
  if (!items || items.length === 0)
    return <p className="text-xs text-gray-400 italic">No info available.</p>;
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5 text-base text-gray-800 leading-[1.6]">
          <span className={`flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full ${dotColor}`} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
};

/* ── Pill / tag row ── */
export const Pills = ({ items, tone = 'teal' }) => {
  const map = {
    teal: 'bg-teal-50 text-teal-800 border-teal-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
    rose: 'bg-rose-50 text-rose-800 border-rose-200',
    purple: 'bg-purple-50 text-purple-800 border-purple-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  };
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((t, i) => (
        <span
          key={i}
          className={`text-sm font-medium px-3 py-1.5 rounded-full border ${map[tone] || map.teal}`}
        >
          {t}
        </span>
      ))}
    </div>
  );
};
